import styles from "./heloc.module.css"

import React from "react"
import cx from "classnames"
import {Wizard} from "activities/components/wizard"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as str from "shared/string"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as env from "util/env"
import {Tooltip} from "shared/tooltips"

const HomeValueSubtitle = ({theme}) => (
  <>
    <div className={cx(styles["subtitle"], fonts.getFontClass())}>
      To get an estimate on your Home Equity Line of Credit, enter the approximate value of your home. If you don't know it, consider looking up the prices of homes for sale in
      your area to get a rough estimate.
    </div>
  </>
)

const HomeValue = ({homeValue, updateState, resetKey, step}) => {
  const question = step.details.question
  return (
    <>
      <div id={`${str.toKebab(question)}`} className={cx(styles["lg-question"], fonts.getFontClass())}>
        {step.details.question}
      </div>
      <inputs.LongCalcInput
        ariaLabelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("homeValue", util.fromString)}
        integerLimit={12}
        initialValue={homeValue}
      />
    </>
  )
}

const OutstandingBalance = ({outstandingBalance, updateState, resetKey, step}) => {
  const question = step.details.question
  return (
    <>
      <div id={`${str.toKebab(question)}`} className={cx(styles["lg-question"], fonts.getFontClass())}>
        {step.details.question}
      </div>
      <inputs.LongCalcInput
        ariaLabelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("outstandingBalance", util.fromString)}
        integerLimit={12}
        initialValue={outstandingBalance}
        allowZero={true}
      />
    </>
  )
}

const calcHelocEstimate = (homeValue, outstandingBalance, ltv) => homeValue * ltv - outstandingBalance

const Results = ({homeValue, outstandingBalance, ltv, updateState, theme, resetKey}) => {
  const helocEstimate = calcHelocEstimate(homeValue, outstandingBalance, ltv / 100)
  return (
    <div className={styles["results-container"]}>
      <div className={styles["results"]}>
        <div className={styles["heloc-estimate"]}>{util.formatDollarsNoCents(helocEstimate)}</div>
        <div className={styles["label"]}>HELOC Estimate</div>
      </div>
      <inputs.RangeInput
        id="ltv-input"
        title="Loan-to-Value Ratio"
        valueType="percent-no-decimal"
        color={theme["color-1"]}
        onChange={updateState("ltv", parseFloat)}
        initialValue={ltv}
        resetKey={resetKey}
        min={60}
        max={100}
        step={5}
        units="%"
      />
    </div>
  )
}

const onChangeStep = (setState, {status, agi, dependents}) => setState({status: status, agi: agi, dependents: dependents})

const isStepComplete = ({agi}) => agi > 0

export default (props) => <HelocCalculator_ {...props} />

const HelocCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey, homeValue, outstandingBalance, ltv} = getState()
    const {steps, theme} = getProps()
    return (
      <Wizard
        className={styles["heloc"]}
        resetKey={resetKey}
        onReset={onReset}
        setup={{
          steps: steps,
          theme: theme,
          props: {
            homeValue: homeValue,
            outstandingBalance: outstandingBalance,
            ltv: ltv,
            theme: theme,
            setState: setState,
            updateState: updateState
          }
        }}
      />
    )
  },
  {
    defaultProps: {
      className: styles["heloc-calculator"],
      steps: [
        {
          title: "HELOC Estimation Calculator",
          subtitle: HomeValueSubtitle,
          isStepComplete: (stepProps) => stepProps.homeValue > 0,
          onChangeStep: (setState, stepProps) => setState({homeValue: stepProps.homeValue}),
          content: HomeValue,
          details: {
            question: "What is the value of your home?"
          },
          buttonText: "Next"
        },
        {
          title: "HELOC Estimation Calculator",
          subtitle: "Enter what you have left to pay on your mortgage including interest.",
          isStepComplete: (stepProps) => Number.isInteger(stepProps.outstandingBalance),
          onChangeStep: (setState, stepProps) => setState({outstandingBalance: stepProps.outstandingBalance}),
          content: OutstandingBalance,
          details: {
            question: "What is the outstanding balance on your mortgage?"
          },
          buttonText: "Next"
        },
        {
          title: "HELOC Estimation Calculator",
          subtitle:
            "The amount you can borrow using a HELOC will depend on your bank or credit union's maximum allowed LTV. LTV (loan-to-value) is the ratio of how much you owe on your home versus how much it's worth. To view your HELOC at different LTVs, adjust the slider below.",
          isStepComplete: () => true,
          content: Results,
          buttonText: "Start Over"
        }
      ]
    },
    stateFilter: (state) => state,
    defaultState: {
      homeValue: 0,
      outstandingBalance: 0,
      ltv: 75
    }
  }
)
